/**
 * The Miles 360 API — the system of record for contacts, coupons and purchases.
 *
 * Nothing in here throws: every call returns a result the caller can branch on.
 * A person mid-flow must never be stopped by a backend hiccup, which is why the
 * only call that blocks the UI is the purchase itself.
 *
 * Base URL is env-driven so the app can be pointed at the `uat` stage without a
 * code change. Next only inlines `process.env.NEXT_PUBLIC_*` when it is written out in full.
 */

import { normalisePhone } from "./lead-prefill";
import type { PlanKey } from "./content";

const DEFAULT_BASE = "https://hn19ywvvng.execute-api.ap-south-1.amazonaws.com/Miles360";

function base() {
  const fromEnv: unknown =
    process.env.NEXT_PUBLIC_MILES360_API_URL;
  const url = typeof fromEnv === "string" && fromEnv.trim() ? fromEnv.trim() : DEFAULT_BASE;
  return url.replace(/\/+$/, "");
}

/**
 * The docs say these endpoints need an `x-api-key`, but the gateway does not
 * enforce it today — calls without one succeed. We send it only when configured,
 * so turning enforcement on later is an env change rather than a code change.
 * Note a NEXT_PUBLIC_ value is inlined into the browser bundle, so any key set here is
 * public (as it already is in the enrolment app's own bundle).
 */
function apiKey() {
  const fromEnv: unknown =
    process.env.NEXT_PUBLIC_MILES360_API_KEY;
  return typeof fromEnv === "string" ? fromEnv.trim() : "";
}

/** The course names the catalog recognises, for each track the app sells. */
const COURSE_BY_TRACK: Record<PlanKey, string> = {
  caira: "CAIRA",
  cpa: "CPA+CAIRA",
  cma: "CMA+CAIRA",
};

export const courseFor = (track: PlanKey) => COURSE_BY_TRACK[track];

export type Ok<T> = { ok: true } & T;
export type Err = { ok: false; status: number; message: string };
export type Result<T> = Ok<T> | Err;

/** Overridable so a slower stage — or a test — can set its own ceiling. */
function timeoutMs() {
  const fromEnv: unknown =
    process.env.NEXT_PUBLIC_MILES360_TIMEOUT_MS;
  const n = Number(fromEnv);
  return Number.isFinite(n) && n > 0 ? n : 10_000;
}

/**
 * Failures come in two shapes: the purchase endpoints use
 * `{success:false, message}`, get-owner-by-phone uses `{error}`.
 */
function messageFrom(body: unknown, fallback: string) {
  if (body && typeof body === "object") {
    for (const k of ["message", "error"] as const) {
      const v = (body as Record<string, unknown>)[k];
      if (typeof v === "string" && v) return v;
    }
  }
  return fallback;
}

async function call<T>(
  path: string,
  // eslint-disable-next-line no-undef -- a DOM type, which the JS rule cannot see
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<Result<T>> {
  const { timeoutMs: override, ...rest } = init;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), override ?? timeoutMs());
  try {
    const res = await fetch(`${base()}${path}`, {
      ...rest,
      signal: controller.signal,
      headers: {
        "content-type": "application/json",
        ...(apiKey() ? { "x-api-key": apiKey() } : {}),
        ...(rest.headers ?? {}),
      },
    });

    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* a body that isn't JSON is only interesting when the call failed */
    }

    if (!res.ok) {
      return { ok: false, status: res.status, message: messageFrom(body, `Request failed (${res.status})`) };
    }
    return { ok: true, ...(body as T) };
  } catch (e) {
    /* An abort and a dead network look the same to the caller: it did not land. */
    const aborted = e instanceof Error && e.name === "AbortError";
    return {
      ok: false,
      status: 0,
      message: aborted ? "The request timed out." : "Could not reach Miles 360.",
    };
  } finally {
    clearTimeout(timer);
  }
}

/* ---------- enquiries ---------- */

/** The CRM course and campaign every enquiry from this page is filed under. */
const ENQUIRY_COURSE_ID = "18";
const ENQUIRY_CAMPAIGN_ID = "2498";

/**
 * The enquiry Lambda routinely takes 10–20s (phone lookup, Salesforce, TechMonk),
 * so the usual ceiling would abandon calls that go on to succeed. 29s is API
 * Gateway's own limit; waiting longer than that buys nothing.
 */
const ENQUIRY_TIMEOUT_MS = 29_000;

/**
 * File the details form as a net enquiry, so the person lands in the CRM the
 * moment they submit. The whole name goes in `first_name`; the form has one
 * name field and splitting it would guess wrong as often as right.
 * `keepalive` lets it finish even if they close the tab while it is in flight.
 */
export function createNetEnquiry(lead: { name: string; phone: string; email: string }) {
  return call<Record<string, unknown>>("/create-net-enquiry", {
    method: "POST",
    keepalive: true,
    timeoutMs: ENQUIRY_TIMEOUT_MS,
    body: JSON.stringify({
      first_name: lead.name.trim(),
      last_name: "",
      phone: normalisePhone(lead.phone),
      country_code: "+91",
      email: lead.email.trim(),
      course_id: ENQUIRY_COURSE_ID,
      campaign_id: ENQUIRY_CAMPAIGN_ID,
    }),
  });
}

/* ---------- coupons ---------- */

type CouponBody = { coupon_code: string; discount_amount: number; expires_at: string };

/**
 * Issue (or re-fetch) this person's reward code. The coupon is a **tracking
 * token only** — its `discount_amount` is zero by design, and the reward the app
 * displays is its own presentation. Never read `discount_amount` for display.
 */
export function generateCoupon(phone: string) {
  return call<CouponBody>("/purchases/generate-coupon", {
    method: "POST",
    body: JSON.stringify({ phone: normalisePhone(phone) }),
  });
}

/* ---------- purchases ---------- */

type PurchaseBody = {
  purchase_uuid: string;
  enrollment_url: string;
  course_name: string;
  /** The contact's assigned owner — their single point of contact at Miles. */
  owner_name?: string | null;
  owner_phone?: string | null;
};

/**
 * Create the purchase and get the enrolment link, where the ₹5,000 reserve is
 * collected. Every call creates a real purchase and a Salesforce enrolment, so
 * callers must guard against a double tap.
 */
export function createPurchase(args: { phone: string; track: PlanKey; couponCode?: string }) {
  const body: Record<string, string> = {
    phone: normalisePhone(args.phone),
    course_name: courseFor(args.track),
  };
  if (args.couponCode) body["coupon_code"] = args.couponCode;
  return call<PurchaseBody>("/purchases/create-purchase-website", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/* ---------- who looks after this person ---------- */

export type Spoc = {
  name: string;
  phone: string;
  email?: string | undefined;
  employeeId?: string | undefined;
};

type OwnerBody = {
  contact_name: string | null;
  owner_name: string | null;
  owner_phone: string | null;
  owner_email: string | null;
  owner_employee_id: string | null;
};

/**
 * Look up the counsellor assigned to a phone number. Used when we do not already
 * hold their details — someone landing on a payment page from a link, say, or on
 * a second device. Note the owner fields are null when a contact has no owner.
 */
export function getOwnerByPhone(phone: string) {
  return call<OwnerBody>(`/get-owner-by-phone?phone=${encodeURIComponent(normalisePhone(phone))}`);
}

/** Only a counsellor we can actually reach is worth showing. */
export function spocFrom(o: {
  owner_name?: string | null;
  owner_phone?: string | null;
  owner_email?: string | null;
  owner_employee_id?: string | null;
}): Spoc | null {
  const name = o.owner_name?.trim() ?? "";
  const phone = normalisePhone(o.owner_phone ?? "");
  if (!name || !phone) return null;
  return {
    name,
    phone,
    email: o.owner_email?.trim() || undefined,
    employeeId: o.owner_employee_id?.trim() || undefined,
  };
}

/* ---------- the journey ---------- */

type PagesBody = { phone: string; pages: string[] };

/**
 * Record one screen. `page` carries its own timestamp — see track.ts.
 * `sourcePageUrl` is the page the person filled the enquiry form on (see
 * store.tsx), captured with the event so a retried post still reports it.
 */
export function recordPage(phone: string, page: string, sourcePageUrl = "") {
  return call<PagesBody>("/discount/page-progress", {
    method: "POST",
    body: JSON.stringify({
      phone: normalisePhone(phone),
      page,
      source_page_url: sourcePageUrl,
    }),
  });
}

export function getPages(phone: string) {
  return call<PagesBody>(`/discount/page-progress?phone=${encodeURIComponent(normalisePhone(phone))}`);
}

/* ---------- quiz results ---------- */

export type CheckResults = {
  phone: string;
  score: number;
  band: string;
  rounds_correct: number;
  mindset_score: number;
  skillset_score: number;
  toolset_score: number;
  reward_earned: number;
  track: PlanKey;
  completed: boolean;
  coupon_code?: string | undefined;
  purchase_uuid?: string | null | undefined;
  utm?: Record<string, string> | undefined;
  /** The page the person filled the enquiry form on; "" when unknown. */
  source_page_url: string;
  answers: Record<string, string | number | boolean>;
};

/** Idempotent on phone: each call replaces the previous state for that contact. */
export function postCheckResults(results: CheckResults) {
  return call<Record<string, never>>("/discount/check-results", {
    method: "POST",
    body: JSON.stringify({ ...results, phone: normalisePhone(results.phone) }),
  });
}
